import { useState } from 'react'
import { ChevronDown, RotateCcw } from 'lucide-react'
import IndicatorConfigModal from '@/components/Indicators/IndicatorConfigModal'
import ColorPickerField from '@/components/Indicators/ColorPickerField'
import SliderField from '@/components/Indicators/SliderField'
import ToggleField from '@/components/Indicators/ToggleField'
import { defaultConfig } from './index'

function Section({ title, children, sectionKey, config, onChange }) {
  const open = config?.panelSections?.[sectionKey] ?? false
  const toggle = () => onChange({ panelSections: { ...(config.panelSections || {}), [sectionKey]: !open } })
  return (
    <div className="border border-[var(--hair,rgba(176,141,87,0.35))] rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-[var(--paper,#FDFDFA)] hover:bg-black/[0.02] transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold,#B08D57)]">{title}</span>
        <ChevronDown size={14} className={`text-[var(--ink-soft,#4A5A52)] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="px-2.5 py-2 space-y-2 bg-white/50 dark:bg-gray-900/20">
          {children}
        </div>
      )}
    </div>
  )
}

const STYLE_KEYS = [
  'colorBull', 'colorBear', 'colorNeutral', 'colorCHoCH', 'colorIFVG', 'colorOTE', 'colorOB', 'colorFvgBear',
  'lineWidth', 'cloudOpacity', 'boxAlpha', 'markerSize', 'haloOpacity',
  'labelFont', 'labelBgOpacity', 'plaqueStyle',
]

export default function ICTQuantumPanel({ config, onChange, onClose }) {
  const cfg = config || {}

  const update = (key, value) => onChange({ ...cfg, [key]: value })
  const updateSection = (section, key, value) => {
    onChange({ ...cfg, [section]: { ...(cfg[section] || {}), [key]: value } })
  }
  const updatePanelSection = (sectionKey, open) => {
    onChange({ panelSections: { ...(cfg.panelSections || {}), [sectionKey]: open } })
  }
  const resetStyle = () => {
    const patch = {}
    for (const k of STYLE_KEYS) patch[k] = defaultConfig[k]
    onChange({ ...cfg, ...patch })
  }

  return (
    <IndicatorConfigModal title="Configuración ICT / SMC Quantum" isOpen onClose={onClose}>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">

        <Section title="Estructura" sectionKey="structure" config={cfg} onChange={onChange}>
          <ToggleField label="Swing BOS/CHoCH" value={cfg.structure?.showSwingStruct ?? true} onChange={(v) => updateSection('structure', 'showSwingStruct', v)} />
          <ToggleField label="Estructura interna" value={cfg.structure?.showInternalStruct ?? true} onChange={(v) => updateSection('structure', 'showInternalStruct', v)} />
          <ToggleField label="Etiquetas HH/HL/LH/LL" value={cfg.structure?.showSwingLabels ?? true} onChange={(v) => updateSection('structure', 'showSwingLabels', v)} />
          <ToggleField label="Strong/Weak" value={cfg.structure?.showStrongWeak ?? true} onChange={(v) => updateSection('structure', 'showStrongWeak', v)} />
          <SliderField label="Swing len" value={cfg.structure?.swingLen ?? 10} min={3} max={50} step={1} onChange={(v) => updateSection('structure', 'swingLen', v)} />
          <SliderField label="Internal len" value={cfg.structure?.internalLen ?? 5} min={2} max={20} step={1} onChange={(v) => updateSection('structure', 'internalLen', v)} />
          <SliderField label="Mín. mov. ATR" value={cfg.structure?.minMoveATRMult ?? 0.5} min={0} max={2} step={0.1} onChange={(v) => updateSection('structure', 'minMoveATRMult', v)} />
        </Section>

        <Section title="Fair Value Gaps" sectionKey="fvg" config={cfg} onChange={onChange}>
          <ToggleField label="FVG (BISI/SIBI)" value={cfg.fvg?.showFVG ?? true} onChange={(v) => updateSection('fvg', 'showFVG', v)} />
          <ToggleField label="iFVG" value={cfg.fvg?.showIFVG ?? true} onChange={(v) => updateSection('fvg', 'showIFVG', v)} />
          <ToggleField label="Línea central" value={cfg.fvg?.showMidline ?? true} onChange={(v) => updateSection('fvg', 'showMidline', v)} />
          <ToggleField label="Etiquetas" value={cfg.fvg?.showLabel ?? true} onChange={(v) => updateSection('fvg', 'showLabel', v)} />
          <SliderField label="Máx FVG" value={cfg.fvg?.maxFVG ?? 10} min={1} max={50} step={1} onChange={(v) => updateSection('fvg', 'maxFVG', v)} />
          <SliderField label="Máx iFVG" value={cfg.fvg?.maxIFVG ?? 5} min={0} max={20} step={1} onChange={(v) => updateSection('fvg', 'maxIFVG', v)} />
          <SliderField label="Filtro ATR" value={cfg.fvg?.minSizeATRMult ?? 0.5} min={0} max={2} step={0.1} onChange={(v) => updateSection('fvg', 'minSizeATRMult', v)} />
        </Section>

        <Section title="Order Blocks" sectionKey="ob" config={cfg} onChange={onChange}>
          <ToggleField label="Mostrar OBs" value={cfg.ob?.showOB ?? true} onChange={(v) => updateSection('ob', 'showOB', v)} />
          <ToggleField label="Línea central" value={cfg.ob?.showMidline ?? true} onChange={(v) => updateSection('ob', 'showMidline', v)} />
          <ToggleField label="Etiquetas" value={cfg.ob?.showLabel ?? true} onChange={(v) => updateSection('ob', 'showLabel', v)} />
          <ToggleField label="Mostrar grado" value={cfg.ob?.showGrade ?? true} onChange={(v) => updateSection('ob', 'showGrade', v)} />
          <SliderField label="Máx por TF" value={cfg.ob?.maxPerTf ?? 10} min={1} max={50} step={1} onChange={(v) => updateSection('ob', 'maxPerTf', v)} />
          <SliderField label="Lookback" value={cfg.ob?.lookback ?? 30} min={5} max={200} step={1} onChange={(v) => updateSection('ob', 'lookback', v)} />
          <SliderField label="Volumen mínimo" value={cfg.ob?.minVolumeGrade ?? 1.2} min={1} max={3} step={0.1} onChange={(v) => updateSection('ob', 'minVolumeGrade', v)} />
        </Section>

        <Section title="Fibonacci" sectionKey="fib" config={cfg} onChange={onChange}>
          <ToggleField label="Mostrar Fib" value={cfg.fib?.showFib ?? true} onChange={(v) => updateSection('fib', 'showFib', v)} />
          <ToggleField label="Mostrar OTE" value={cfg.fib?.showOTE ?? true} onChange={(v) => updateSection('fib', 'showOTE', v)} />
        </Section>

        <Section title="Multi-Timeframe" sectionKey="htf" config={cfg} onChange={onChange}>
          <ToggleField label="Activar HTF" value={cfg.multiTimeframe?.enableHTF ?? true} onChange={(v) => updateSection('multiTimeframe', 'enableHTF', v)} />
          <SliderField label="HTF EMA" value={cfg.multiTimeframe?.htfEMALen ?? 50} min={10} max={200} step={1} onChange={(v) => updateSection('multiTimeframe', 'htfEMALen', v)} />
          {/* "Requerir alineación HTF" retirado del panel (lote ICT 2026-09-02,
              spec de Marc): en ESTE indicador (registry ict_quantum, cálculo en
              cliente) la clave multiTimeframe.requireHTFAlignment no la lee
              ningún consumidor — control muerto. Sigue existiendo en el
              defaultConfig (sin migración: es una pref inofensiva) y en el
              panel legacy ICTConfigPanel de ChartPage, donde SÍ viaja a la
              API (require_htf_alignment → ict_strategy.generate_ict_signal). */}
        </Section>

        <Section title="Señal" sectionKey="signal" config={cfg} onChange={onChange}>
          <ToggleField label="Mostrar señal" value={cfg.signal?.showSignal ?? true} onChange={(v) => updateSection('signal', 'showSignal', v)} />
          <ToggleField label="Stop Loss" value={cfg.signal?.showSL ?? true} onChange={(v) => updateSection('signal', 'showSL', v)} />
          <ToggleField label="TP1" value={cfg.signal?.showTP1 ?? true} onChange={(v) => updateSection('signal', 'showTP1', v)} />
          <ToggleField label="TP2" value={cfg.signal?.showTP2 ?? true} onChange={(v) => updateSection('signal', 'showTP2', v)} />
          <ToggleField label="TP3" value={cfg.signal?.showTP3 ?? true} onChange={(v) => updateSection('signal', 'showTP3', v)} />
          <SliderField label="Score mínimo" value={cfg.signal?.minScore ?? 4} min={1} max={12} step={1} onChange={(v) => updateSection('signal', 'minScore', v)} />
          <SliderField label="R mínimo" value={cfg.signal?.minRR ?? 1.2} min={0.5} max={5} step={0.1} onChange={(v) => updateSection('signal', 'minRR', v)} />
          <SliderField label="Extensión caja (barras)" value={cfg.signal?.extensionBars ?? 10} min={2} max={100} step={1} onChange={(v) => updateSection('signal', 'extensionBars', v)} />
        </Section>

        <Section title="Colores" sectionKey="colors" config={cfg} onChange={onChange}>
          <ColorPickerField label="Alcista" value={cfg.colorBull ?? '#1B5E20'} onChange={(v) => update('colorBull', v)} />
          <ColorPickerField label="Bajista" value={cfg.colorBear ?? '#B71C1C'} onChange={(v) => update('colorBear', v)} />
          <ColorPickerField label="Neutro" value={cfg.colorNeutral ?? '#374151'} onChange={(v) => update('colorNeutral', v)} />
          <ColorPickerField label="CHoCH" value={cfg.colorCHoCH ?? '#B71C1C'} onChange={(v) => update('colorCHoCH', v)} />
          <ColorPickerField label="iFVG" value={cfg.colorIFVG ?? '#C2410C'} onChange={(v) => update('colorIFVG', v)} />
          <ColorPickerField label="OTE" value={cfg.colorOTE ?? '#B45309'} onChange={(v) => update('colorOTE', v)} />
          <ColorPickerField label="OB" value={cfg.colorOB ?? '#1D4ED8'} onChange={(v) => update('colorOB', v)} />
          <ColorPickerField label="SIBI FVG" value={cfg.colorFvgBear ?? '#C2410C'} onChange={(v) => update('colorFvgBear', v)} />
          <SliderField label="Grosor líneas" value={cfg.lineWidth ?? 1.5} min={1} max={3} step={0.5} unit="px" onChange={(v) => update('lineWidth', v)} />
        </Section>

        <Section title="Estilo avanzado" sectionKey="style" config={cfg} onChange={onChange}>
          <SliderField label="Opacidad de cajas" value={cfg.boxAlpha ?? 0.18} min={0} max={0.5} step={0.01} onChange={(v) => update('boxAlpha', v)} />
          <SliderField label="Opacidad de nube/fondo" value={cfg.cloudOpacity ?? 0.18} min={0} max={0.5} step={0.01} onChange={(v) => update('cloudOpacity', v)} />
          <SliderField label="Tamaño de marcadores" value={cfg.markerSize ?? 1} min={0} max={3} step={1} onChange={(v) => update('markerSize', v)} />
          <SliderField label="Opacidad de halo" value={cfg.haloOpacity ?? 0.25} min={0} max={1} step={0.01} onChange={(v) => update('haloOpacity', v)} />
          <SliderField label="Opacidad fondo etiquetas" value={cfg.labelBgOpacity ?? 0.12} min={0} max={0.5} step={0.01} onChange={(v) => update('labelBgOpacity', v)} />
          <div className="space-y-1">
            <span className="text-xs text-[var(--ink-soft,#4A5A52)]">Fuente de etiquetas</span>
            <input
              type="text"
              value={cfg.labelFont ?? defaultConfig.labelFont}
              onChange={(e) => update('labelFont', e.target.value)}
              className="w-full px-2 py-1 text-[11px] rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-white/80 dark:bg-gray-900/50 text-[var(--ink,#15211B)]"
            />
          </div>
          <button
            onClick={resetStyle}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
          >
            <RotateCcw size={12} />
            Restablecer estilo Quantum
          </button>
        </Section>

      </div>
    </IndicatorConfigModal>
  )
}
